import type { ReactNode, SVGProps } from 'react';

function Fig({
  children,
  titulo,
  legenda,
  className = '',
}: {
  children: ReactNode;
  titulo: string;
  legenda?: string;
  className?: string;
}): ReactNode {
  return (
    <figure className={`overflow-hidden rounded-mc-card border border-mc-line bg-mc-surface/60 ${className}`}>
      <figcaption className="border-b border-mc-line px-4 py-2.5 text-sm font-medium text-mc-ink">
        {titulo}
      </figcaption>
      <div className="p-4">{children}</div>
      {legenda ? (
        <p className="border-t border-mc-line px-4 py-2 text-xs text-mc-ink3">{legenda}</p>
      ) : null}
    </figure>
  );
}

function Svg({
  children,
  viewBox = '0 0 640 320',
  ...rest
}: SVGProps<SVGSVGElement> & { viewBox?: string }): ReactNode {
  return (
    <svg viewBox={viewBox} role="img" aria-hidden="true" {...rest}>
      {children}
    </svg>
  );
}

/** Ciclo: pergunta → ver resposta → classificar → próxima revisão. */
export function FluxoEstudo(): ReactNode {
  return (
    <Fig
      titulo="Ciclo de uma revisão"
      legenda="Cada cartão passa por este ciclo. O intervalo até a próxima aparição depende do botão que você escolher."
    >
      <Svg className="mx-auto w-full max-w-2xl text-mc-ink2">
        <defs>
          <marker id="seta" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
            <path d="M0,0 L6,3 L0,6 Z" fill="currentColor" />
          </marker>
        </defs>
        <rect x="20" y="120" width="120" height="56" rx="12" fill="var(--mc-accent-soft)" stroke="var(--mc-accent)" />
        <text x="80" y="152" textAnchor="middle" fill="var(--mc-ink)" fontSize="14" fontWeight="600">
          Pergunta
        </text>
        <line x1="140" y1="148" x2="168" y2="148" stroke="currentColor" markerEnd="url(#seta)" />
        <rect x="168" y="120" width="120" height="56" rx="12" fill="var(--mc-surface-hover)" stroke="currentColor" />
        <text x="228" y="152" textAnchor="middle" fill="var(--mc-ink)" fontSize="14" fontWeight="600">
          Ver resposta
        </text>
        <line x1="288" y1="148" x2="316" y2="148" stroke="currentColor" markerEnd="url(#seta)" />
        <rect x="316" y="108" width="140" height="76" rx="12" fill="var(--mc-warning-soft)" stroke="var(--mc-warning)" />
        <text x="386" y="140" textAnchor="middle" fill="var(--mc-ink)" fontSize="13" fontWeight="600">
          Escolher botão
        </text>
        <text x="386" y="162" textAnchor="middle" fill="var(--mc-ink3)" fontSize="11">
          Errei · Difícil · Bom · Fácil
        </text>
        <line x1="456" y1="148" x2="484" y2="148" stroke="currentColor" markerEnd="url(#seta)" />
        <rect x="484" y="120" width="136" height="56" rx="12" fill="var(--mc-accent2-soft)" stroke="var(--mc-accent2)" />
        <text x="552" y="144" textAnchor="middle" fill="var(--mc-ink)" fontSize="13" fontWeight="600">
          Próxima
        </text>
        <text x="552" y="164" textAnchor="middle" fill="var(--mc-ink3)" fontSize="11">
          revisão agendada
        </text>
        <path
          d="M552 176 Q552 240 80 240 Q80 200 80 176"
          fill="none"
          stroke="currentColor"
          strokeDasharray="6 4"
          markerEnd="url(#seta)"
        />
        <text x="320" y="268" textAnchor="middle" fill="var(--mc-ink3)" fontSize="12">
          Quando chegar a hora, o cartão volta à fila
        </text>
      </Svg>
    </Fig>
  );
}

/** Linha do tempo com intervalos crescendo. */
export function LinhaTempoIntervalos(): ReactNode {
  const marcos = [
    { dia: 'Hoje', rotulo: '1º Bom', sub: '1 dia' },
    { dia: '+2 d', rotulo: 'Bom', sub: '2 dias' },
    { dia: '+5 d', rotulo: 'Bom', sub: '5 dias' },
    { dia: '+12 d', rotulo: 'Bom', sub: '12 dias' },
    { dia: '+30 d', rotulo: 'Bom', sub: '30 dias' },
  ];
  return (
    <Fig
      titulo="Intervalos crescem com acertos consistentes"
      legenda="Exemplo: sempre marcar Bom num cartão novo. Cada marcação multiplica o intervalo anterior por 2,5 (arredondado para baixo)."
    >
      <Svg className="mx-auto w-full max-w-2xl text-mc-accent">
        <line x1="40" y1="160" x2="600" y2="160" stroke="currentColor" strokeWidth="3" />
        {marcos.map((m, i) => {
          const x = 40 + i * 140;
          return (
            <g key={m.dia}>
              <circle cx={x} cy={160} r="10" fill="var(--mc-surface)" stroke="currentColor" strokeWidth="3" />
              <text x={x} y={130} textAnchor="middle" fill="var(--mc-ink)" fontSize="13" fontWeight="600">
                {m.dia}
              </text>
              <text x={x} y={200} textAnchor="middle" fill="var(--mc-ink2)" fontSize="12">
                {m.rotulo}
              </text>
              <text x={x} y={218} textAnchor="middle" fill="var(--mc-ink3)" fontSize="11">
                próx.: {m.sub}
              </text>
            </g>
          );
        })}
      </Svg>
    </Fig>
  );
}

/** Quatro botões estilizados como na tela de estudo. */
export function DiagramaBotoes(): ReactNode {
  const botoes = [
    { rotulo: 'Errei', cor: 'var(--mc-danger)', prev: '10 min', tecla: '1' },
    { rotulo: 'Difícil', cor: 'var(--mc-warning)', prev: '1 d', tecla: '2' },
    { rotulo: 'Bom', cor: 'var(--mc-accent)', prev: '1 d', tecla: '3' },
    { rotulo: 'Fácil', cor: 'var(--mc-accent2)', prev: '4 d', tecla: '4' },
  ];
  return (
    <Fig titulo="Os quatro botões (cartão novo, intervalo 0)">
      <div className="grid gap-3 sm:grid-cols-2">
        {botoes.map((b) => (
          <div
            key={b.rotulo}
            className="flex items-center justify-between rounded-mc-field border px-4 py-3"
            style={{ borderColor: b.cor, background: `color-mix(in srgb, ${b.cor} 12%, transparent)` }}
          >
            <div>
              <p className="font-medium" style={{ color: b.cor }}>
                {b.rotulo}
              </p>
              <p className="text-xs text-mc-ink3">Tecla {b.tecla}</p>
            </div>
            <span className="mc-mono text-sm text-mc-ink2">→ {b.prev}</span>
          </div>
        ))}
      </div>
    </Fig>
  );
}

/** Árvore de decisão simplificada. */
export function ArvoreDecisao(): ReactNode {
  return (
    <Fig titulo="Qual botão usar?" legenda="Seja honesto: subestimar dificulta o aprendizado; superestimar atrasa revisões úteis.">
      <Svg className="mx-auto w-full max-w-xl text-mc-ink2">
        <rect x="240" y="16" width="160" height="44" rx="10" fill="var(--mc-surface-hover)" stroke="currentColor" />
        <text x="320" y="44" textAnchor="middle" fill="var(--mc-ink)" fontSize="14" fontWeight="600">
          Vi a resposta?
        </text>
        <line x1="280" y1="60" x2="120" y2="100" stroke="currentColor" />
        <line x1="360" y1="60" x2="520" y2="100" stroke="currentColor" />
        <text x="180" y="88" textAnchor="middle" fill="var(--mc-danger)" fontSize="12" fontWeight="600">
          Não sabia
        </text>
        <text x="460" y="88" textAnchor="middle" fill="var(--mc-accent)" fontSize="12" fontWeight="600">
          Sabia
        </text>
        <rect x="40" y="100" width="160" height="48" rx="10" fill="var(--mc-danger-soft)" stroke="var(--mc-danger)" />
        <text x="120" y="130" textAnchor="middle" fill="var(--mc-ink)" fontSize="13" fontWeight="600">
          Errei
        </text>
        <rect x="440" y="100" width="160" height="48" rx="10" fill="var(--mc-accent-soft)" stroke="var(--mc-accent)" />
        <text x="520" y="124" textAnchor="middle" fill="var(--mc-ink)" fontSize="12">
          Quão fácil foi?
        </text>
        <line x1="500" y1="148" x2="355" y2="190" stroke="currentColor" />
        <line x1="520" y1="148" x2="465" y2="190" stroke="currentColor" />
        <line x1="540" y1="148" x2="575" y2="190" stroke="currentColor" />
        <rect x="310" y="190" width="90" height="40" rx="8" fill="var(--mc-warning-soft)" stroke="var(--mc-warning)" />
        <text x="355" y="215" textAnchor="middle" fill="var(--mc-ink)" fontSize="12">
          Difícil
        </text>
        <rect x="420" y="190" width="90" height="40" rx="8" fill="var(--mc-accent-soft)" stroke="var(--mc-accent)" />
        <text x="465" y="215" textAnchor="middle" fill="var(--mc-ink)" fontSize="12">
          Bom
        </text>
        <rect x="530" y="190" width="90" height="40" rx="8" fill="var(--mc-accent2-soft)" stroke="var(--mc-accent2)" />
        <text x="575" y="215" textAnchor="middle" fill="var(--mc-ink)" fontSize="12">
          Fácil
        </text>
      </Svg>
    </Fig>
  );
}

/** Esboço do painel de desempenho. */
export function EsbocoDesempenho(): ReactNode {
  return (
    <Fig titulo="Painel Desempenho — o que você encontra lá">
      <div className="grid gap-3 sm:grid-cols-2">
        {[
          { rotulo: 'Sequência', valor: '12 dias', desc: 'Dias seguidos estudando' },
          { rotulo: 'Meta de hoje', valor: '18/20', desc: 'Cartões revisados hoje' },
          { rotulo: 'Taxa de acerto', valor: '84%', desc: 'Últimos 30 dias' },
          { rotulo: 'Vencendo', valor: '43', desc: 'Cartões para revisar em breve' },
        ].map((c) => (
          <div key={c.rotulo} className="rounded-mc-field border border-mc-line bg-mc-surface/40 p-4">
            <p className="mc-mono text-xs text-mc-ink3">{c.rotulo}</p>
            <p className="mt-1 font-mc-display text-2xl font-semibold text-mc-accent">{c.valor}</p>
            <p className="mt-1 text-xs text-mc-ink3">{c.desc}</p>
          </div>
        ))}
      </div>
    </Fig>
  );
}

/** Cronograma visual — barras SVG (visível em qualquer tema). */
export function EsbocoCronograma(): ReactNode {
  const dias = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
  const cargas = [24, 18, 31, 12, 45, 8, 15];
  const max = Math.max(...cargas);
  const chartBase = 130;
  const chartH = 88;
  const barW = 36;
  const gap = 38;
  const startX = 48;

  return (
    <Fig
      titulo="Cronograma — revisões previstas por dia"
      legenda="Barras mais altas = mais cartões agendados para aquele dia"
    >
      <Svg viewBox="0 0 640 180" className="mx-auto w-full max-w-2xl text-mc-accent">
        <line
          x1="32"
          y1={chartBase}
          x2="608"
          y2={chartBase}
          stroke="var(--mc-line)"
          strokeWidth="2"
        />
        {dias.map((d, i) => {
          const h = Math.max(6, (cargas[i]! / max) * chartH);
          const x = startX + i * (barW + gap);
          const y = chartBase - h;
          return (
            <g key={d}>
              <rect
                x={x}
                y={y}
                width={barW}
                height={h}
                rx={4}
                fill="var(--mc-accent)"
                opacity={0.78}
              />
              <text
                x={x + barW / 2}
                y={chartBase + 22}
                textAnchor="middle"
                fill="var(--mc-ink3)"
                fontSize="11"
              >
                {d}
              </text>
            </g>
          );
        })}
      </Svg>
    </Fig>
  );
}
